<template>
    <div class="congress-schedule-event-card">
        <!-- Collapsible Header -->
        <div class="congress-schedule-event-card-header" @click="toggleExpanded">
            <div class="congress-schedule-event-card-header-inner">
                <div class="congress-schedule-event-card-header-inner-left">
                    <icon
                        icon="phosphor/regular/arrows-out-cardinal"
                        class="congress-schedule-event-card-header-move cursor-grab"
                    ></icon>
                </div>
                <div class="congress-schedule-event-card-header-label">
                    <span class="font-medium">
                        {{ ticket.name || lang('congresses::congresses.tickets.untitled') }}
                    </span>
                    <span v-if="ticket.price" class="text-sm text-gray-500 dark:text-gray-400 ml-2">
                        {{ ticket.price }} {{ ticket.currency }}
                    </span>
                    <span
                        v-if="ticket.is_active === 'active'"
                        class="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                    >
                        {{ lang('congresses::congresses.tickets.active') }}
                    </span>
                    <span
                        v-else-if="ticket.is_active === 'passive'"
                        class="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                    >
                        {{ lang('congresses::congresses.tickets.passive') }}
                    </span>
                    <span
                        v-else-if="ticket.is_active === 'hidden'"
                        class="ml-2 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                    >
                        {{ lang('congresses::congresses.tickets.hidden') }}
                    </span>
                    <UiBadge v-if="ticket.is_vip" color="amber" class="ml-2">
                        {{ lang('congresses::congresses.tickets.is_vip') }}
                    </UiBadge>
                </div>
                <div class="congress-schedule-event-card-header-actions">
                    <Button type="button" size="sm" color="danger" variant="inline" @click.stop="$emit('remove')">
                        <span class="action-icon">
                            <Icon icon="phosphor/regular/trash"></Icon>
                        </span>
                        <span class="action-label">
                            {{ lang('congresses::congresses.tickets.remove') }}
                        </span>
                    </Button>
                    <icon
                        icon="hero/outline/chevron-down"
                        class="congress-schedule-event-card-header-actions-icon"
                        :class="{ 'rotate-180': expanded }"
                    ></icon>
                </div>
            </div>
        </div>

        <!-- Expanded Content -->
        <div v-show="expanded" class="event-content mt-2 space-y-2 px-5">
            <div class="grid grid-cols-2 gap-5">
                <Field
                    v-model="ticket.available_from"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.tickets.available_from'),
                        slug: `available_from_${ticket._id}`,
                        type: 'date',
                        adminComponent: 'AdminDate',
                        nullable: true,
                    }"
                >
                    <template #help>
                        {{ lang('congresses::congresses.tickets.available_from_help') }}
                    </template>
                </Field>
                <Field
                    v-model="ticket.available_to"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.tickets.available_to'),
                        slug: `available_to_${ticket._id}`,
                        type: 'date',
                        adminComponent: 'AdminDate',
                        nullable: true,
                    }"
                >
                    <template #help>
                        {{ lang('congresses::congresses.tickets.available_to_help') }}
                    </template>
                </Field>
            </div>

            <div class="grid grid-cols-2 gap-5">
                <Field
                    v-model="ticket.name"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.tickets.name'),
                        slug: `name_${ticket._id}`,
                        type: 'text',
                        adminComponent: 'AdminText',
                        required: true,
                    }"
                >
                    <template #help>
                        {{ lang('congresses::congresses.tickets.name_help') }}
                    </template>
                </Field>
                <Field
                    v-model="ticket.is_active"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.tickets.is_active'),
                        slug: `is_active_${ticket._id}`,
                        type: 'select',
                        adminComponent: 'AdminSelect',
                        portal: true,
                        options: [
                            { id: 'active', name: lang('congresses::congresses.tickets.active') },
                            { id: 'passive', name: lang('congresses::congresses.tickets.passive') },
                            { id: 'hidden', name: lang('congresses::congresses.tickets.hidden') },
                        ],
                    }"
                >
                    <template #help>
                        {{ lang('congresses::congresses.tickets.is_active_help') }}
                    </template>
                </Field>
            </div>

            <Field
                v-model="ticket.description"
                label-position="top"
                :field="{
                    name: lang('congresses::congresses.tickets.description'),
                    slug: `description_${ticket._id}`,
                    type: 'textarea',
                    adminComponent: 'AdminTextarea',
                }"
            >
                <template #help>
                    {{ lang('congresses::congresses.tickets.description_help') }}
                </template>
            </Field>

            <div class="grid grid-cols-2 gap-5">
                <Field
                    v-model="ticket.price"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.tickets.price'),
                        slug: `price_${ticket._id}`,
                        type: 'number',
                        adminComponent: 'AdminNumber',
                        required: true,
                    }"
                >
                    <template #help>
                        {{ lang('congresses::congresses.tickets.price_help') }}
                    </template>
                </Field>
                <Field
                    v-model="ticket.hide_price"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.tickets.hide_price'),
                        slug: `hide_price_${ticket._id}`,
                        type: 'checkbox',
                        adminComponent: 'AdminCheckbox',
                    }"
                >
                    <template #help>
                        {{ lang('congresses::congresses.tickets.hide_price_help') }}
                    </template>
                </Field>
            </div>

            <div class="grid grid-cols-2 gap-5">
                <Field
                    v-model="ticket.is_vip"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.tickets.is_vip'),
                        slug: `is_vip_${ticket._id}`,
                        type: 'checkbox',
                        adminComponent: 'AdminCheckbox',
                    }"
                >
                    <template #help>
                        {{ lang('congresses::congresses.tickets.is_vip_help') }}
                    </template>
                </Field>
            </div>

            <!--

                <Field
                    v-model="ticket.currency"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.tickets.currency'),
                        slug: `currency_${ticket._id}`,
                        type: 'text',
                        adminComponent: 'AdminText',
                        required: true,
                    }"
                />
                -->

            <!--

                <Field
                    v-model="ticket.type"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.tickets.type'),
                        slug: `type_${ticket._id}`,
                        type: 'text',
                        adminComponent: 'AdminText',
                        required: true,
                    }"
                />
            -->
            <!--
            <Field
                v-model="ticket.is_active"
                size="sm"
                label-position="top"
                :field="{
                    name: lang('congresses::congresses.tickets.is_active'),
                    slug: `is_active_${ticket._id}`,
                    type: 'checkbox',
                    adminComponent: 'AdminCheckbox',
                }"
            />
            -->
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { lang } from '@/lang/composable';
import Button from '@src/Common/Components/Button.vue';
import Field from '@src/Common/Components/Field.vue';
import type { Ticket } from '../types';

defineProps<{
    index: number;
}>();

defineEmits<{
    (e: 'remove'): void;
}>();

const ticket = defineModel<Ticket>();

const expanded = ref(false);

const toggleExpanded = () => {
    expanded.value = !expanded.value;
};
</script>
